export interface SeoInfo {
  title: string;
  description: string;
  imageUrl?: string;
}

interface SeoContainerInterface {
  [key: string]: SeoInfo;
}

const SeoImageUrls = {
  primary:
    'https://cdn.sanity.io/images/7skkgjh0/production/51917da38abe5035586ff1e3df36be41f7a655b1-1200x798.jpg',
  starterBox:
    'https://cdn.sanity.io/images/7skkgjh0/production/b20f7801feaee31f140048bf4dae57cfd1a0ca61-1040x690.jpg',
  wetFood:
    'https://cdn.sanity.io/images/7skkgjh0/production/79f9578dcf7190ee41e36d06f1a238b2ab1532bf-1000x787.jpg',
  dryFood:
    'https://cdn.sanity.io/images/7skkgjh0/production/f59522b7ff0ef521d5ccab7b852fe1769457d3b7-1000x667.jpg',
  goodnessBlends:
    'https://cdn.sanity.io/images/7skkgjh0/production/26043cc118bffd24ed13b31120d6b269bd2fe624-2849x1935.jpg'
};

const PageSeo: SeoContainerInterface = {
  'home': {
    title: 'Real, Healthy Cat Food Made Simple | Cat Person',
    description:
      'No grains or fillers. Just simple, high-protein food for cats and the people who love them. Start a plan now and get grain-free cat food delivered monthly.',
    imageUrl: SeoImageUrls.primary
  },
  'meal-plan': {
    title: 'Meal Plans | Easier, Healthier Cat Food Delivery | Cat Person',
    description:
      'Meals made healthier for them, Meal Plans made easier for you. Get natural, grain-free cat food delivered monthly at 10% off. Edit, delay, or cancel any time.',
    imageUrl: SeoImageUrls.starterBox
  },
  'about': {
    title: 'About | Healthy Cat Food Without the Fine Print | Cat Person',
    description:
      '19 varieties of protein-packed, grain-free cat food, delivered. Plus bowls, beds, and more, all designed to meet your cat’s needs and your aesthetic standards.',
    imageUrl: SeoImageUrls.primary
  },
  'how-it-works': {
    title: 'How It Works | Cat Food Subscriptions | Cat Person',
    description:
      'Tell us about your cat; we’ll customize their Meal Plan. Get high-protein meals delivered every 4 weeks at 10% off with our subscription cat food service.',
    imageUrl: SeoImageUrls.primary
  },
  'food-philosophy': {
    title: 'Our Food Philosophy | Cat Person',
    description:
      'No grains, no fillers, no fine print. Our simple, healthy cat food only has ingredients your cat needs to thrive—like 2.5x more protein than AAFCO standards.',
    imageUrl: SeoImageUrls.primary
  },
  'transition-guide': {
    title: 'How to Transition Your Cat to New Food | Cat Person',
    description:
      'It’s important to give your cat’s tummy and tastebuds time to adjust. Here’s how to safely transition your cat to our high-protein, grain-free cat food.',
    imageUrl: SeoImageUrls.primary
  }
};

const CollectionSeo: SeoContainerInterface = {
  'wet-food': {
    title: 'High-Protein, Grain-Free Wet Food | Cat Person',
    description:
      'Packed with high-quality meat and totally grain-free, our 16 varieties of Paté and Shreds in Broth wet food have everything your cat needs to thrive.',
    imageUrl: SeoImageUrls.wetFood
  },
  'dry-food': {
    title: 'High-Protein, Grain-Free Dry Food for Cats | Cat Person',
    description:
      'What makes our dry cat food different? It’s super high in protein, grain-free, and contains no weird fillers. Upgrade your cat to our healthy kibble today.',
    imageUrl: SeoImageUrls.dryFood
  },
  'treats': {
    title: 'Healthy, Grain-Free Cat Supplements',
    description:
      'Want to support your cat’s health? Treat them to Goodness Blends, tasty purées of real meat and hardworking nutrients. Available in 5 targeted health benefits.',
    imageUrl: SeoImageUrls.goodnessBlends
  },
  'mesa-bowl': {
    title: 'Mesa Bowl | Modern Cat Bowl and Spill-Catching Tray | Cat Person',
    description:
      'Our Mesa Bowl is ergonomically designed for your cat’s comfort—the shallow bowl prevents whisker fatigue and the elevated base reduces strain from crouching.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/227ba28a789d621f8e176f902b32d700a3fe64aa-2984x1894.jpg'
  },
  'canopy-bed': {
    title: 'Canopy Bed | Modern 3-in-1 Cat Bed | Cat Person',
    description:
      'Designed with a felt canopy and machine-washable cushion, our 3-in-1 cat bed adjusts to your cat’s preferences and the elevated design looks great in any home.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/ee64c3060020447db46fc2cdf6d1562fd67cd4d6-1000x1000.jpg'
  },
  'toys': {
    title: 'Interactive Catnip Wand Toy for Cats | Cat Person',
    description:
      'Keep your kitty entertained with premium, interactive cat toys that have it all: a wand, string, plush toy, and everyone’s favorite—catnip. Shop now.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/6eac90383458b37c5d3cce2f317b5a52d2f9469b-1200x600.jpg'
  },
  'cat-person-bucket-hat': {
    title: 'Cat Person Bucket Hat  | Cat Person',
    description:
      'Protect your face from the sun while flaunting your bond. Shop the limited edition Cat Person Bucket Hat, made from 100% cotton.',
    imageUrl:
      'https://cdn.shopify.com/s/files/1/0270/2150/8682/products/CatPersonBucketHatBlack_CollectionPage.jpg'
  }
};

const ProductSeo: SeoContainerInterface = {
  'wet-food': CollectionSeo['wet-food'],
  'dry-food': CollectionSeo['dry-food'],
  'goodness-blends': CollectionSeo['treats'],
  'catnip-toy': CollectionSeo['toys'],
  'bed': CollectionSeo['canopy-bed'],
  'mesa-bowl': CollectionSeo['mesa-bowl'],
  'cat-person-bucket-hat': CollectionSeo['cat-person-bucket-hat'],
  'cat-person-hoodie': {
    title: 'Cat Person Hoodie in Black | Cat Person',
    description:
      'Hoodie for you, hoodie strings for your cat. Made from 100% organic cotton fleece, the Cat Person Hoodie in Black is perfect for lounging with your furry BFF.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/cf57b8eab622933530b6a264f258793d2d8e583f-2560x3842.jpg'
  },
  'cat-hoodie': {
    title: 'Cat Hoodie in Black | Cat Person',
    description:
      'Made from 100% US-sourced cotton, the Cat Hoodie is guaranteed to make your cat look 100% adorable. Buy it with a matching Cat Person Hoodie & save $10.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/6e18dbcbfeb1fb26c93b407c8df51890aa80b1ca-2560x3840.jpg'
  },
  'cat-person-hoodie-cream': {
    title: 'Cat Person Hoodie in Cream | Cat Person',
    description:
      'Cozy, 100% cotton, and still looks good when it’s covered in cat hair. Shop the Cat Person Hoodie in Cream and save when you buy it with a matching Cat Hoodie.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/f21cd01ac07123cb478e148d60ab7eea27d36a90-1600x2000.jpg'
  },
  'cat-hoodie-cream': {
    title: 'Cat Hoodie in Cream | Cat Person',
    description:
      'For cats who are proud to be cats. Shop the 100% cotton Cat Hoodie in Cream and save 10% when you bundle it with the matching Cat Person Hoodie..',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/3561f2942a16a717471d56b9919fe215e91f23d7-1600x1999.jpg'
  },
  'beanie': {
    title: 'Cat Person Embroidered Cotton Beanie | Cat Person',
    description:
      'Keep your head toasty while flaunting your bond in our 100% cotton Cat Person Beanie. Part of a limited edition merch collection, so shop now while you can.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/5b5b8fd4d1057cea231da1a2d16e4fa2fa9c7e4c-2560x3840.jpg'
  },
  'cat-person-baseball-cap': {
    title: 'Cat Person Embroidered Baseball Cap | Cat Person',
    description:
      'In case of bad fur days. Flaunt your cat person pride with the embroidered, 100% cotton Cat Person Baseball Cap, part of a limited edition merch collection.',
    imageUrl:
      'https://cdn.sanity.io/images/7skkgjh0/production/1dad0d49b6679d8a879b4db89f3924bbfa3e5227-1600x2000.jpg'
  },
  'signature-hoodie': {
    title: 'Signature Cat Hoodie | Cat Person',
    description:
      'This 100% GOTS Certified Organic Cotton hoodie features a special cat illustration.'
  },
  'litter-box': {
    title: 'Modern Covered Litter Box | Skylight Litter Box | Cat Person',
    description:
      'The modern Skylight Litter Box features a ventilated wood lid, matte black exterior, and accessible corner entry.'
  },
  'litter-scoop': {
    title: 'Metal Litter Scoop | Hideaway Scoop | Cat Person',
    description:
      'Keep your scooper handy yet hidden with this long-handled cat litter scoop and matching holder. Made from durable aluminum alloy and pine wood handle.'
  },
  'dog-toy': {
    title: 'Jason Catnip Pounce Toy | Cat Person',
    description:
      'Entertain your kitty with this catnip pounce toy. This dog-shaped toy features a string “leash” to tease your cat.'
  }
};

const VariantSeo: SeoContainerInterface = {
  'chicken-pate': {
    title: 'Chicken Paté Wet Food Recipe | Cat Person',
    description:
      'Keep your kitty’s pantry stocked with Cat Person’s Chicken Paté, a healthy, protein-packed wet food recipe made with real chicken and zero grains or fillers.',
    imageUrl: SeoImageUrls.wetFood
  },
  'chicken-shreds-in-broth': {
    title: 'Chicken Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'Made with real chicken and zero grains, our best selling Chicken Shreds in Broth supplies your cat with all the nutrients they need. Start your cat food subscription today.',
    imageUrl: SeoImageUrls.wetFood
  },
  'chicken-turkey-pate': {
    title: 'Chicken & Turkey Paté Wet Food Recipe | Cat Person',
    description:
      'Made with real, high-quality meat—not grains and fillers. Try Cat Person’s grain-free Chicken & Turkey  Paté and save when you start a cat food subscription.',
    imageUrl: SeoImageUrls.wetFood
  },
  'chicken-turkey-shreds-in-broth': {
    title: 'Chicken & Turkey Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'We pack every cup of our Chicken & Turkey Shreds in Broth with real, high-quality meat—not grains and fillers. It’s the healthy cat food your feline deserves.',
    imageUrl: SeoImageUrls.wetFood
  },
  'tuna-pate': {
    title: 'Tuna Paté Wet Food Recipe | Cat Person',
    description:
      'Satisfy your cat’s cravings and nutritional needs with our grain-free Tuna Paté, a healthy cat food option made with high-quality, sustainably sourced seafood.',
    imageUrl: SeoImageUrls.wetFood
  },
  'tuna-shreds-in-broth': {
    title: 'Tuna Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'Healthier, meatier, convenient-ier cat food. Cat Person’s Tuna Shreds in Broth is grain-free, low carb, and made with sustainably sourced seafood.',
    imageUrl: SeoImageUrls.wetFood
  },
  'tuna-salmon-pate': {
    title: 'Tuna & Salmon Paté Wet Food Recipe | Cat Person',
    description:
      'Feed your cat the protein they need with our Tuna & Salmon Paté. This natural cat food recipe is grain-free, high-protein, and made with sustainably sourced seafood.',
    imageUrl: SeoImageUrls.wetFood
  },
  'tuna-salmon-shreds-in-broth': {
    title: 'Tuna Salmon Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'Our #1 Tuna Salmon Shreds in Broth wet food has 95% more protein than AAFCO standards for healthier, leaner cats. Try the grain-free food that has cats purring.',
    imageUrl: SeoImageUrls.wetFood
  },
  'mackerel-bream-pate': {
    title: 'Mackerel & Bream Paté Wet Food Recipe | Cat Person',
    description:
      'Give your cat a taste of the good stuff with Cat Person’s Mackerel & Bream Paté. Our natural cat food is grain-free and made with real, high-quality protein.',
    imageUrl: SeoImageUrls.wetFood
  },
  'salmon-mackerel-pate': {
    title: 'Salmon & Mackerel Paté Wet Food Recipe | Cat Person',
    description:
      'Feed your cat a grain-free, high-protein diet with our Salmon & Mackerel Paté, made with zero fillers and sustainably sourced seafood. ',
    imageUrl: SeoImageUrls.wetFood
  },
  'salmon-mackerel-shreds-in-broth': {
    title: 'Salmon & Mackerel Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'Give your cat a taste of the good stuff with Cat Person’s Salmon & Mackerel Shreds in Broth. Our cat food is grain-free and made with high-quality seafood.',
    imageUrl: SeoImageUrls.wetFood
  },
  'beef-pate': {
    title: 'Beef Paté Wet Food Recipe | Cat Person',
    description:
      'Looking for chicken-free cat food? Look no further than Cat Person’s Beef Paté, a high-protein, grain-free wet food option made with real beef.',
    imageUrl: SeoImageUrls.wetFood
  },
  'beef-shreds-in-broth': {
    title: 'Beef Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'Never question what’s in your cat’s food. Our Beef Shreds in Broth is grain-free and uses beef as its only protein source, making it easy to avoid allergens.',
    imageUrl: SeoImageUrls.wetFood
  },
  'chicken-duck-pate': {
    title: 'Chicken & Duck Paté Wet Food Recipe | Cat Person',
    description:
      'Keep your cat happy and healthy with our protein-packed, grain-free Chicken & Duck Paté. Start your cat food subscription today and save 10% on every order.',
    imageUrl: SeoImageUrls.wetFood
  },
  'chicken-duck-shreds-in-broth': {
    title: 'Chicken & Duck Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'Looking for healthy cat food? Try Cat Person’s Chicken & Duck Shreds in Broth. It’s high-protein, grain-free, and packed with real chicken and duck.',
    imageUrl: SeoImageUrls.wetFood
  },

  'duck-pate': {
    title: 'Duck Paté Wet Food Recipe | Cat Person',
    description:
      'Cat Person’s Duck Paté makes it easy to avoid common food allergens like chicken. Plus, all our cat food is natural, grain-free, and packed with protein.',
    imageUrl: SeoImageUrls.wetFood
  },
  'duck-shreds-in-broth': {
    title: 'Duck Shreds in Broth Wet Food Recipe | Cat Person',
    description:
      'Does your cat have food allergies? Try Duck Shreds in Broth. Our grain-free recipe uses duck as the only protein source, making it easy to avoid allergens.',
    imageUrl: SeoImageUrls.wetFood
  },
  'chicken-turkey': {
    title: 'Chicken & Turkey Dry Food Recipe | Cat Person',
    description:
      'Some cats like crunch. That’s why we made Chicken & Turkey Dry Food, so your cat can get the grain-free, high-protein diet they need and the crunch they love.',
    imageUrl: SeoImageUrls.dryFood
  },
  'duck-turkey': {
    title: 'Duck & Turkey Dry Food Recipe | Cat Person',
    description:
      'It’s not just kibble; it’s healthy, grain-free, super high-protein kibble. Try Cat Person’s Duck & Turkey Dry Food Recipe save when you start a Meal Plan.',
    imageUrl: SeoImageUrls.dryFood
  },
  'salmon-tuna': {
    title: 'Salmon & Tuna Dry Food Recipe | Cat Person',
    description:
      'Treat your cat to the good stuff. You know, the grain-free, high-protein good stuff made with sustainable seafood. That’s our Salmon & Tuna Dry Food Recipe.',
    imageUrl: SeoImageUrls.dryFood
  },
  'cognitive-health': {
    title: 'Cat Supplements for Cognitive Health | Cat Person',
    description:
      'Give your cat a nutrient boost with our Cognitive Health Goodness Blends—a tasty cat supplement that supports your cat’s brain development and health.',
    imageUrl: SeoImageUrls.goodnessBlends
  },
  'immune-support': {
    title: 'Cat Supplements for Immune Support | Cat Person',
    description:
      'Boost your cat’s health and immunity with our Immune Support Goodness Blends, a tasty purée of meat and nutrients like Vitamin C and Omega-3 Fatty Acids.',
    imageUrl: SeoImageUrls.goodnessBlends
  },
  'skin-coat': {
    title: 'Cat Supplements for Skin & Coat Health | Cat Person',
    description:
      'Support healthy skin and shiny fur with Skin & Coat Goodness Blends, supplements made with real meat and nutrients like Zinc, Omega-3, and Omega-6 Fatty Acids.',
    imageUrl: SeoImageUrls.goodnessBlends
  },
  'digestive-support': {
    title: 'Cat Supplements for Digestive Support | Cat Person',
    description:
      'Show your cat some extra love with our grain-free Digestive Support cat supplements. These tasty treats are made with real meat and help support a healthy gut.',
    imageUrl: SeoImageUrls.goodnessBlends
  },
  'bone-joint': {
    title: 'Cat Supplements for Bone & Joint Health | Cat Person',
    description:
      'Keep your feline friend healthy and active with our Bone & Joint Goodness Blends. These tasty cat supplements help support bone health and joint mobility.',
    imageUrl: SeoImageUrls.goodnessBlends
  }
};

export const pageSeoInfo = (page: string): SeoInfo | undefined => {
  return PageSeo[page];
};

export const collectionSeoInfo = (product: string): SeoInfo | undefined => {
  return CollectionSeo[product];
};

export const variantSeoInfo = (product: string, variant?: string): SeoInfo | undefined => {
  const productSeo = ProductSeo[product];
  const variantSeo = variant ? VariantSeo[variant] : undefined;
  return variantSeo ?? productSeo;
};
